import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { paypalHandlersPropTypes } from "components/payments/propTypes";
import PaymentError from "components/payments/PaymentError";

/* Paypal Handler functions
  validate: it is *not* called at validation time. It
            is only called *once* when the PayPal JS lib is first loaded. A more accurate name would be
            `onPaypalScriptLoaded`. See:
            - https://github.com/paypal/paypal-checkout/issues/392#issuecomment-311571546
            - https://github.com/paypal/paypal-checkout/issues/139#issuecomment-293146410

            params: paypalActions
              has 2 functions
              enable: enables the paypal button
              disable: disables the paypal button

            Only free tier and reactivation flows will need this. The user has to select a plan first.
            All other flows will have the plan already set.

            This should probably be used to get the paypalActions into the parent component to allow
            for enabling and disabling the paypal button based on the validity of the parent.

  onClick: Called when the Paypal button is clicked. Even if the button is disabled via the validate function
           this function will still be called, the Paypal pop up will not open.

           Not sure we need to use this. It looked useful when using vanilla js, but React will allow us to handle
           a lot of the validation requirements outside the Paypal button.

  payment: Called when the Paypal button is clicked IF the button is enabled. This is used to set up the payment.
           Currently we make a POST ajax request to /paypal/billing_agreements with the subcription (plan type, annual or monthly)
           and the proration_date for the upgrade flow (going from monthly to annual). The response from that request contains
           the token

           params: resolve, reject
            resolve - called with the token returned from the server
            reject - called with error from the request to /paypal/billing_agreements if it fails

  onAuthorize: called after payment is resolved. The first param data will contain a paymentToken. This is used (along with rest of form data)
               to post to / (registrations#create). When being posted, paymentToken is called paypal_token. It's used to create a PayAttempt.
               If the PayAttempt is in a pending state by the end of the request, the front end must poll the server until it either succeeds
               or fails. The returned pay_attempt will have a `url` key to use.

               params: data, paypalActions
                 data: {
                         billingToken:
                         button_version:
                         intent:
                         orderID:
                         paymentID:
                         paymentToken:
                         returnUrl:
                       }
                 paypalActions: doesn't seem to be used

  onCancel: called when the user cancels the paypal flow.

  onError: This is called when the payment handler is rejected.

  See app/assets/javascripts/lc4/payment.js and app/assets/javascripts/lc4/signup_subscribe.js for
  current implement details.

  Incomplete Paypal docs: https://developer.paypal.com/docs/archive/checkout/how-to/customize-flow/
*/

const style = {
  color: "gold", // small | medium | large | responsive
  height: 55, // gold | blue | silver | black
  label: "pay", // pill | rect
  layout: "horizontal", // checkout | credit | pay | buynow | generic
  shape: "rect", // (removes "The safer, easier way to pay" tagline below button),
  size: "responsive",
  tagline: false
};

export default function PaypalButton(props) {
  const {
    validate,
    onClick,
    payment,
    onAuthorize,
    onCancel,
    onError,
    processing,
    disabled,
    errorMessage,
    showSeparator
  } = props;
  const buttonContainer = useRef(null);
  useEffect(() => {
    const env = props.environment === "live" ? "production" : "sandbox";
    window.paypal.Button.render(
      {
        env,
        onAuthorize,
        onCancel,
        onClick,
        onError,
        payment,
        style,
        validate
      },
      buttonContainer.current
    );
  }, []);
  return (
    <>
      <PaymentError errorMessage={errorMessage} testId="paypal-error" />
      <div className="paypal-button-holder">
        <div
          ref={buttonContainer}
          id="lc-paypal-button-container"
          className="lc-paypal-button stripe-updates"
          style={{ display: processing ? "none" : "block" }}
        />
        <button
          disabled
          type="button"
          className="btn btn-primary btn-lg form-button form-submit"
          style={{ display: processing ? "block" : "none", width: "100%" }}
        >
          <i className="fa fa-cog fa-spin fa-fw" />
          PayPal processing&hellip;
        </button>
        <div
          className="paypal-disabled-overlay"
          style={{ display: disabled ? "block" : "none" }}
        />
      </div>
      {showSeparator && <Separator />}
    </>
  );
}

PaypalButton.propTypes = {
  environment: PropTypes.oneOf(["live", "sandbox"]).isRequired,
  processing: PropTypes.bool.isRequired,
  showSeparator: PropTypes.bool,
  ...paypalHandlersPropTypes
};

// Might not need validation depending on what flow the user is in
/* eslint-disable react/default-props-match-prop-types */
// This fails despite the fact that that the proptypes for these are being spread above
PaypalButton.defaultProps = {
  onClick: () => {},
  showSeparator: true,
  validate: () => {}
};
/* eslint-enable react/default-props-match-prop-types */

function Separator(_props) {
  return (
    <div className="separator">
      <hr />
      <span>or</span>
      <hr />
    </div>
  );
}
